import React, { useState, useEffect, useRef } from 'react'
import './style.css'

import HoverTooltip from '../../../../components/HoverTooltip'

type DataPoint = {
    label: string
    value: number
}

interface PlanChartProps {
    reg_number: string
    theme: string
}

const LineChart: React.FC<PlanChartProps> = ({ reg_number, theme }) => {
    const [data, setData] = useState<DataPoint[]>([])
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    const pathRef = useRef<SVGPathElement | null>(null)
    const animationPlayed = useRef(false)

    useEffect(() => {
        if (reg_number === 'Нет данных') {
            setData([])
            setIsLoading(false)
            return
        }

        animationPlayed.current = false
        setIsLoading(true)

        fetch(
            process.env.REACT_APP_API_URL +
                `/api/backend/v2/plan_positions_count_by_month/?reg_number=${reg_number}`,
            {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`)
                }
                return response.json()
            })
            .then((result) => {
                const scaledResult = result.map((d: DataPoint) => ({
                    label: d.label,
                    value: d.value,
                }))
                setData(scaledResult)
            })
            .catch((error) => {
                console.error('Error fetching data:', error)
                setData([])
            })
            .finally(() => {
                setIsLoading(false)
            })
    }, [reg_number])

    useEffect(() => {
        if (isLoading || data.length === 0 || animationPlayed.current) return

        const pathElement = pathRef.current
        if (pathElement) {
            const length = pathElement.getTotalLength()
            pathElement.style.transition = 'none'
            pathElement.style.strokeDasharray = `${length} ${length}`
            pathElement.style.strokeDashoffset = `${length}`
            pathElement.getBoundingClientRect()
            pathElement.style.transition = 'stroke-dashoffset 2s ease'
            pathElement.style.strokeDashoffset = '0'
        }

        const circles =
            document.querySelectorAll<SVGCircleElement>('.line-point')

        circles.forEach((circle, index) => {
            circle.style.opacity = '0'
            circle.style.transition = `opacity 0.4s ease ${(index + 1) * 0.1}s`

            requestAnimationFrame(() => {
                circle.style.opacity = '1'
            })
        })

        animationPlayed.current = true
    }, [isLoading, data])

    const width = 800
    const height = 340
    const offsetXLeft = 50
    const offsetXRight = 50
    const padding = 40
    const paddingY = 30

    const maxValue = data.length > 0 ? Math.max(...data.map((d) => d.value)) : 0

    const getNiceMax = (value: number) => {
        if (value <= 0) return 5

        const magnitude = Math.pow(10, Math.floor(Math.log10(value)))
        const normalized = value / magnitude

        let niceNormalized = 1
        if (normalized <= 1) niceNormalized = 1
        else if (normalized <= 2) niceNormalized = 2
        else if (normalized <= 5) niceNormalized = 5
        else niceNormalized = 10

        return niceNormalized * magnitude
    }

    const maxDataValue = getNiceMax(maxValue)
    const yStep = maxDataValue / 5

    const scaleX = (index: number) => {
        if (data.length <= 1) {
            return width / 2
        }

        return (
            paddingY +
            offsetXLeft +
            (index * (width - 2 * paddingY - offsetXLeft - offsetXRight)) /
                (data.length - 1)
        )
    }

    const scaleY = (value: number) =>
        height - padding - (value * (height - 2 * padding)) / maxDataValue

    const pathD = data
        .map(
            (point, i) =>
                `${i === 0 ? 'M' : 'L'} ${scaleX(i)} ${scaleY(point.value)}`
        )
        .join(' ')

    return (
        <div className="flex flex-col w-full h-full relative">
            <div className="flex flex-row text-[#181818] dark:text-[#FFFFFF] text-[16px]">
                <div className="flex flex-1">Количество закупок (ед.)</div>
                <div style={{ cursor: 'pointer' }}>
                    <button className="w-5 h-5">
                        <HoverTooltip
                            tooltipContent={
                                <div className="flex flex-col w-[300px] p-2 gap-2 text-justify text-[11px]">
                                    <span>
                                        Это график количества закупок по
                                        месяцам.
                                    </span>
                                </div>
                            }
                            align="left"
                            valign="middle"
                        >
                            <svg
                                id="info-circle"
                                data-name="Layer 1"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    fill={
                                        theme == 'dark' ? '#FFFFFF' : '#181818'
                                    }
                                    d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1.25,1.25,0,0,0,12,7.5Z"
                                />
                            </svg>
                        </HoverTooltip>
                    </button>
                </div>
            </div>

            {reg_number === 'Нет данных' ? (
                <div className="flex text-[#181818] dark:text-[#FFFFFF] text-[16px] w-full h-full items-center justify-center">
                    Нет данных
                </div>
            ) : isLoading ? (
                <div className="flex flex-row text-[#181818] dark:text-[#FFFFFF] text-[16px] w-full h-full items-center justify-center gap-2">
                    <svg
                        className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                        />
                        <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                    </svg>
                    <span>Загрузка данных</span>
                </div>
            ) : (
                <div className="flex flex-row relative">
                    <div className="flex items-center">
                        <svg
                            width={width}
                            height={height}
                            className="flex w-full h-full"
                            viewBox={`0 0 ${width} ${height}`}
                        >
                            {[...Array(6)].map((_, i) => (
                                <line
                                    key={`grid-line-${i}`}
                                    x1={padding}
                                    y1={scaleY(yStep * i)}
                                    x2={width - padding}
                                    y2={scaleY(yStep * i)}
                                    stroke="gray"
                                    strokeOpacity="0.2"
                                />
                            ))}

                            {data.map((_, i) => (
                                <line
                                    key={`grid-line-x-${i}`}
                                    x1={scaleX(i)}
                                    y1={height - padding}
                                    x2={scaleX(i)}
                                    y2={padding}
                                    stroke="gray"
                                    strokeOpacity="0.2"
                                />
                            ))}

                            <line
                                x1={padding}
                                y1={height - padding}
                                x2={padding}
                                y2={padding}
                                stroke={theme == 'dark' ? '#464447' : '#dbdee2'}
                                strokeWidth={2}
                            />

                            <line
                                x1={padding}
                                y1={height - padding}
                                x2={width - padding}
                                y2={height - padding}
                                stroke={theme == 'dark' ? '#464447' : '#dbdee2'}
                                strokeWidth={2}
                            />

                            {data.map((point, i) => (
                                <g key={`x-label-${i}`}>
                                    <line
                                        x1={scaleX(i)}
                                        y1={height - padding - 5}
                                        x2={scaleX(i)}
                                        y2={height - padding + 5}
                                        stroke={
                                            theme == 'dark'
                                                ? '#464447'
                                                : '#dbdee2'
                                        }
                                        strokeWidth={2}
                                    />
                                    <text
                                        x={scaleX(i)}
                                        y={height - padding + 20}
                                        textAnchor="middle"
                                        fontSize={10}
                                        fill={
                                            theme == 'dark'
                                                ? '#FFFFFF'
                                                : '#181818'
                                        }
                                    >
                                        {point.label}
                                    </text>
                                </g>
                            ))}

                            {[...Array(6)].map((_, i) => (
                                <g key={`y-label-${i}`}>
                                    <line
                                        x1={padding - 5}
                                        y1={scaleY(yStep * i)}
                                        x2={padding + 5}
                                        y2={scaleY(yStep * i)}
                                        stroke={
                                            theme == 'dark'
                                                ? '#464447'
                                                : '#dbdee2'
                                        }
                                        strokeWidth={2}
                                    />
                                    <text
                                        x={padding - 10}
                                        y={scaleY(yStep * i) + 4}
                                        textAnchor="end"
                                        fontSize={10}
                                        fill={
                                            theme == 'dark'
                                                ? '#FFFFFF'
                                                : '#181818'
                                        }
                                    >
                                        {Math.round(yStep * i)}
                                    </text>
                                </g>
                            ))}

                            {data.length > 0 && (
                                <path
                                    ref={pathRef}
                                    d={pathD}
                                    fill="none"
                                    stroke={
                                        theme == 'dark' ? '#649A3B' : '#83d2b7'
                                    }
                                    strokeWidth={2}
                                    style={{
                                        strokeDasharray: 'none',
                                        strokeDashoffset: '0',
                                    }}
                                />
                            )}

                            {data.map((point, i) => (
                                <HoverTooltip
                                    key={`point-tooltip-${i}`}
                                    tooltipContent={
                                        <div className="text-[14px]">
                                            {point.value}
                                        </div>
                                    }
                                    align="center"
                                    valign="top"
                                >
                                    <circle
                                        className="line-point"
                                        cx={scaleX(i)}
                                        cy={scaleY(point.value)}
                                        r={hoveredIndex === i ? 7 : 5}
                                        fill={
                                            hoveredIndex === i
                                                ? theme == 'dark'
                                                    ? '#a2c685'
                                                    : '#d6f0e7'
                                                : theme == 'dark'
                                                  ? '#649A3B'
                                                  : '#83d2b7'
                                        }
                                        onMouseEnter={() => setHoveredIndex(i)}
                                        onMouseLeave={() =>
                                            setHoveredIndex(null)
                                        }
                                    />
                                </HoverTooltip>
                            ))}
                        </svg>
                    </div>
                </div>
            )}
        </div>
    )
}

export default LineChart
